import { SecurityGroup } from './configuration/security.group';
import { ArrayUtilities } from './utilities/array.utilities';
import { UserManagementService } from './services/user-management.sevice';
import { WellKnownClaims } from './web-tokens/well-known-claims';

/**
 * Represents an authenticated user
 */
export class User {
  public groups: Array<string> = [];
  public firstName: string = '';
  public lastName: string = '';
  public userId: string = '';
  public roles: Array<string> = [];
  public displayName: string | null = null;
  public racf: string | null = null;
  public source: string | null = null;
  public type: string | null = null;
  public title: string | null = null;
  public department: string | null = null;
  public emails: Array<string> | null = null;
  public phoneNumber: string | null = null;
  public workAddress: string | null = null;
  public workPostalCode: string | null = null;
  public workState: string | null = null;
  public workCity: string | null = null;

  /**
   * Creates a new user
   */
  constructor(userManager: UserManagementService, groupMappings: Array<SecurityGroup> | null) {
    userManager.onPropertiesUpdated.subscribe(
      (map: Map<WellKnownClaims, any> | null) => {
        if (map === null) {
          this.updateUnauthorized();
        } else {
          this.updateUser(map, groupMappings);
        }
      }
    );
  }

  /**
   * Updates the user to the unauthorized values
   */
  private updateUnauthorized(): void {
    this.userId = '';
    this.department = null;
    this.displayName = 'Anonymous';
    this.emails = null;
    this.firstName = '';
    this.lastName = '';
    this.groups = [];
    this.phoneNumber = null;
    this.racf = null;
    this.roles = [];
    this.source = null;
    this.title = null;
    this.type = null;
    this.workAddress = null;
    this.workCity = null;
    this.workPostalCode = null;
    this.workState = null;
  }

  /**
   * Updates a user with the updated user properties
   * @param properties A map of updated properties 
   * @param groupMappings The list of group mappings to use for group resolution
   */
  private updateUser(properties: Map<WellKnownClaims, any>, groupMappings: Array<SecurityGroup> | null): void {
    let roles: string[] = [];
    let groups: string[] = [];
    let displayName: string | null = null;

    properties.forEach((value: any, key: WellKnownClaims) => {
      switch (key) {
        case WellKnownClaims.Email:
          if (typeof value === 'string') {
            this.emails = [value];
          } else {
            this.emails = value;
          }
          break;
        case WellKnownClaims.Roles:
          roles = value;
          break;
        case WellKnownClaims.DisplayName:
          displayName = value;
          break;
        case WellKnownClaims.Department:
          this.department = value;
          break;
        case WellKnownClaims.FirstName:
          this.firstName = value;
          break;
        case WellKnownClaims.LastName:
          this.lastName = value;
          break;
        case WellKnownClaims.PhoneNumber:
          this.phoneNumber = value;
          break;
        case WellKnownClaims.Racf:
          this.racf = value;
          break;
        case WellKnownClaims.Title:
          this.title = value;
          break;
        case WellKnownClaims.UserId:
          this.userId = value;
          break;
        case WellKnownClaims.UserSource:
          this.source = value;
          break;
        case WellKnownClaims.UserType:
          this.type = value;
          break;
        case WellKnownClaims.WorkAddress:
          this.workAddress = value;
          break;
        case WellKnownClaims.WorkCity:
          this.workCity = value;
          break;
        case WellKnownClaims.WorkPostalCode:
          this.workPostalCode = value;
          break;
        case WellKnownClaims.WorkState:
          this.workState = value;
          break;
        default:
          break;
      }
    });

    if ((displayName === null || displayName === '') && this.firstName != null && this.lastName != null) {
      displayName = this.lastName + ', ' + this.firstName;
    }

    if (groupMappings) {
      for (let group of groupMappings) {
        if (ArrayUtilities.hasIntersection(group.roles, roles, this.securityComparer)) {
          groups.push(group.name);
        }
      }
    }

    this.displayName = displayName;
    this.roles = roles;
    this.groups = groups;
  }

  /**
   * Determines if the user has one of the listed roles
   * @param roles The roles to check
   * @returns True if the user has the role otherwise false
   */
  public isRoleAuthorized(roles: string[]): boolean {
    try {
      return ArrayUtilities.hasIntersection(this.roles, roles, this.securityComparer);
    } catch (error) {
      console.error('Failed to verify role membership because an error occurred.', error);
      return false;
    }
  }

  /**
   * Determines if the user has one of the listed groups
   * @param roles The groups to check
   * @returns True if the user has the group otherwise false
   */
  public isGroupAuthorized(groups: string[]): boolean {
    try {
      return ArrayUtilities.hasIntersection(this.groups, groups, this.securityComparer);
    } catch (error) {
      console.error('Failed to verify group membership because an error occurred.', error);
      return false;
    }
  }

  private securityComparer(first: string, second: string): boolean {
    return first.toLowerCase() === second.toLowerCase();
  }
}
